import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Send, Paperclip, X, CheckCircle2 } from 'lucide-react';
import { API } from '@/constants/data';

const NEED_OPTIONS = ['Evento', 'Instalação permanente', 'Locação de equipamentos', 'Outro'];
const SERVICES = ['Sonorização', 'Painéis de LED', 'Projeção', 'Iluminação', 'Transmissão/Gravação', 'Informática', 'Estruturas', 'Totens/Holografia', 'Outros'];
const MIC_TYPES = ['Sem fio', 'Com fio', 'Lapela', 'Headset'];

const inputClass = "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-[#0A2463] placeholder-slate-400 focus:border-[#00A8E8] focus:ring-1 focus:ring-[#00A8E8] focus:outline-none transition-colors font-body disabled:bg-slate-50 disabled:text-slate-400";
const labelClass = "block text-sm font-bold text-[#0A2463] font-body mb-1.5";
const checkClass = "flex items-center gap-2 text-sm text-[#475569] font-body cursor-pointer";

const SpecBlock = ({ title, orientKey, v, set, children, testId }) => (
  <div className="border border-slate-200 rounded-2xl p-4 sm:p-5 bg-slate-50/50" data-testid={testId}>
    <p className="font-heading text-[#0A2463] uppercase text-sm tracking-wide mb-3">{title}</p>
    <div className={v[orientKey] ? 'opacity-40 pointer-events-none space-y-4' : 'space-y-4'}>
      {children}
    </div>
    <label className={`${checkClass} mt-3`}>
      <input type="checkbox" checked={v[orientKey] || false} onChange={set(orientKey)} data-testid={`${testId}-orientacao`} />
      Preciso de orientação técnica
    </label>
  </div>
);

const ContactSection = () => {
  const [v, setV] = useState({ servicos: [] });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const set = (key) => (e) => setV(s => ({ ...s, [key]: e.target?.type === 'checkbox' ? e.target.checked : e.target.value }));
  const toggleList = (key, item) => setV(s => {
    const list = s[key] || [];
    return { ...s, [key]: list.includes(item) ? list.filter(x => x !== item) : [...list, item] };
  });

  const need = v.need || '';
  const isInstalacao = need === 'Instalação permanente';
  const servicos = v.servicos || [];
  const has = (s) => servicos.includes(s);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError('Arquivo muito grande (máx. 10MB)'); return; }
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/attachment`, fd);
      setAttachment({ url: res.data.url, name: res.data.filename });
    } catch {
      setError('Falha ao anexar o arquivo. Tente novamente ou envie sem anexo.');
    }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const spec = (cond, label, val) => (cond && val ? `  • ${label}: ${val}` : null);
    const lines = [
      `Como podemos ajudar?: ${need}`,
      v.empresa ? `Empresa/Instituição: ${v.empresa}` : null,
      v.cnpj ? `CNPJ: ${v.cnpj}` : null,
      v.nomeProjeto ? `Nome do evento/projeto: ${v.nomeProjeto}` : null,
      isInstalacao && v.ambiente ? `Tipo de ambiente: ${v.ambiente}` : null,
      isInstalacao && v.finalidade ? `Finalidade: ${v.finalidade}` : null,
      isInstalacao && v.prazo ? `Prazo de implantação: ${v.prazo}` : null,
      isInstalacao ? `Já possui planta do local?: ${v.planta || 'Não informado'}` : null,
      `Local previsto de execução: ${v.local || 'Não informado'}`,
      `Sala(s)/ambiente(s): ${v.salas || 'Não informado'}`,
      `Data prevista: ${v.dataInicio || '?'} a ${v.dataFim || '?'}`,
      `Horário previsto: ${v.horaInicio || '?'} às ${v.horaFim || '?'}`,
      `Público estimado: ${v.publico || 'Não informado'}`,
      `Atividades simultâneas em mais de um ambiente: ${v.simultaneas || 'Não informado'}`,
      servicos.length ? `Serviços necessários: ${servicos.join(', ')}` : null,
      has('Sonorização') ? `Sonorização:${v.sonOrient ? ' precisa de orientação técnica' : ''}` : null,
      spec(has('Sonorização') && !v.sonOrient, 'Qtd. microfones', v.sonQtd),
      spec(has('Sonorização') && !v.sonOrient, 'Tipos de microfone', (v.sonTipos || []).join(', ')),
      spec(has('Sonorização') && !v.sonOrient, 'Áudio para', v.sonUso),
      has('Painéis de LED') ? `Painéis de LED:${v.ledOrient ? ' precisa de orientação técnica' : ''}` : null,
      spec(has('Painéis de LED') && !v.ledOrient, 'Qtd. painéis', v.ledQtd),
      spec(has('Painéis de LED') && !v.ledOrient, 'Dimensões (L x A)', v.ledDim),
      spec(has('Painéis de LED') && !v.ledOrient, 'Uso', v.ledLocal),
      has('Projeção') ? `Projeção:${v.projOrient ? ' precisa de orientação técnica' : ''}` : null,
      spec(has('Projeção') && !v.projOrient, 'Qtd. telas/projetores', v.projQtd),
      spec(has('Projeção') && !v.projOrient, 'Dimensões desejadas', v.projDim),
      has('Transmissão/Gravação') ? `Transmissão/Gravação:${v.transOrient ? ' precisa de orientação técnica' : ''}` : null,
      spec(has('Transmissão/Gravação') && !v.transOrient, 'Tipo', v.transTipo),
      spec(has('Transmissão/Gravação') && !v.transOrient, 'Plataforma', v.transPlataforma),
      spec(has('Transmissão/Gravação') && !v.transOrient, 'Qtd. ambientes', v.transAmbientes),
      has('Estruturas') ? `Estruturas:${v.estrOrient ? ' precisa de orientação técnica' : ''}` : null,
      spec(has('Estruturas') && !v.estrOrient, 'Tipo de estrutura', v.estrTipo),
      spec(has('Estruturas') && !v.estrOrient, 'Quantidade', v.estrQtd),
      spec(has('Estruturas') && !v.estrOrient, 'Dimensões', v.estrDim),
      `Detalhes: ${v.detalhes || 'Não informado'}`,
    ].filter(Boolean);
    try {
      await axios.post(`${API}/contact`, {
        name: v.nome || '',
        email: v.email || 'nao@informado.com',
        phone: v.whatsapp || '',
        company: v.empresa || null,
        cnpj: v.cnpj || null,
        service: need,
        message: lines.join('\n'),
        attachment_url: attachment?.url || null,
        form_type: 'orcamento'
      });
      setSuccess(true);
      setV({ servicos: [] });
      setAttachment(null);
    } catch {
      setError('Não foi possível enviar agora. Seus dados foram preservados — tente novamente em instantes ou fale conosco pelo WhatsApp.');
    } finally { setLoading(false); }
  };

  return (
    <section id="contato" className="py-16 sm:py-20 lg:py-28 bg-[#F8FAFC]" data-testid="contact-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="text-center mb-8">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#0A2463] uppercase">Solicite seu Orçamento</h2>
          <p className="text-[#475569] text-sm sm:text-base font-body mt-3 max-w-xl mx-auto">
            Conte o que sua empresa precisa. Nossa equipe ajuda a definir a solução ideal.
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="relative rounded-t-3xl overflow-hidden h-[150px] sm:h-[180px]">
            <img src="https://static.prod-images.emergentagent.com/jobs/ea405bf3-7d8d-4b70-bd85-c90f31cfd218/images/4f4194eeaea47c8f20e562b3aed079ec36ebe4fed376c44204b2fcf4bb73f982.jpeg"
              alt="Auditório com palco de LED - Grupo Tomaselli"
              className="w-full h-full object-cover object-[center_72%] sm:object-[center_62%]" />
            <div className="absolute inset-0 bg-[#0A2463]/25" />
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-24 bg-gradient-to-r from-transparent via-black/45 to-transparent blur-sm" />
              <p className="relative text-white font-heading text-xl sm:text-3xl uppercase tracking-wider" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.55)' }}>
                Seu projeto começa aqui.
              </p>
            </div>
          </motion.div>

          {success ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center justify-center text-center p-10 bg-white border border-slate-200 rounded-b-3xl shadow-lg" data-testid="contact-success">
              <div className="w-20 h-20 rounded-full bg-[#25D366]/15 flex items-center justify-center mb-6">
                <CheckCircle2 className="text-[#25D366]" size={44} />
              </div>
              <h3 className="font-heading text-3xl text-[#0A2463] uppercase mb-2">Solicitação recebida!</h3>
              <p className="text-[#475569] font-body max-w-md">
                Obrigado! Nossa equipe vai analisar as informações e retornar <strong>em até 1 dia útil</strong> pelo WhatsApp ou e-mail informado com a proposta ou eventuais dúvidas.
              </p>
              <button onClick={() => setSuccess(false)} data-testid="new-message-btn"
                className="mt-6 bg-[#0A2463] text-white px-8 py-3 rounded-full font-bold font-body">Nova solicitação</button>
            </motion.div>
          ) : (
            <motion.form onSubmit={handleSubmit} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              data-testid="contact-form"
              className="bg-white border border-slate-200 border-t-0 rounded-b-3xl p-6 sm:p-10 space-y-5 shadow-lg">

              {/* 1. Como podemos ajudar? */}
              <div>
                <label className={labelClass}>Como podemos ajudar? *</label>
                <select required value={need} onChange={set('need')} className={inputClass} data-testid="field-need">
                  <option value="">Selecione</option>
                  {NEED_OPTIONS.map(op => <option key={op} value={op}>{op}</option>)}
                </select>
              </div>

              {/* 2. Dados de contato */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>Empresa ou instituição</label>
                  <input type="text" value={v.empresa || ''} onChange={set('empresa')} className={inputClass} data-testid="field-empresa" />
                </div>
                <div>
                  <label className={labelClass}>CNPJ</label>
                  <input type="text" value={v.cnpj || ''} onChange={set('cnpj')} placeholder="00.000.000/0000-00" className={inputClass} data-testid="field-cnpj" />
                </div>
                <div>
                  <label className={labelClass}>Seu nome *</label>
                  <input type="text" required value={v.nome || ''} onChange={set('nome')} className={inputClass} data-testid="field-nome" />
                </div>
                <div>
                  <label className={labelClass}>WhatsApp com DDD *</label>
                  <input type="tel" required value={v.whatsapp || ''} onChange={set('whatsapp')} placeholder="(92) 90000-0000" className={inputClass} data-testid="field-whatsapp" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>E-mail de contato *</label>
                  <input type="email" required value={v.email || ''} onChange={set('email')} className={inputClass} data-testid="field-email" />
                </div>
              </div>

              {/* Informações do projeto — sempre visíveis */}
              <div data-testid="fields-projeto">
                <p className="text-xs text-[#475569] font-body mb-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5">
                  Caso as informações ainda não estejam confirmadas, indique uma previsão.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <label className={labelClass}>Nome do evento ou projeto</label>
                    <input type="text" value={v.nomeProjeto || ''} onChange={set('nomeProjeto')} className={inputClass} data-testid="field-nome-projeto" />
                  </div>
                  <div>
                    <label className={labelClass}>Local previsto de execução</label>
                    <input type="text" value={v.local || ''} onChange={set('local')}
                      placeholder="Nome do espaço ou estabelecimento" className={inputClass} data-testid="field-local" />
                  </div>
                  <div>
                    <label className={labelClass}>Sala(s) ou ambiente(s)</label>
                    <input type="text" value={v.salas || ''} onChange={set('salas')}
                      placeholder="Informar quais serão utilizados" className={inputClass} data-testid="field-salas" />
                  </div>
                  <div>
                    <label className={labelClass}>Data prevista de início</label>
                    <input type="date" value={v.dataInicio || ''} onChange={set('dataInicio')} className={inputClass} data-testid="field-data-inicio" />
                  </div>
                  <div>
                    <label className={labelClass}>Data prevista de término</label>
                    <input type="date" value={v.dataFim || ''} onChange={set('dataFim')} className={inputClass} data-testid="field-data-fim" />
                  </div>
                  <div>
                    <label className={labelClass}>Horário previsto de início</label>
                    <input type="time" value={v.horaInicio || ''} onChange={set('horaInicio')} className={inputClass} data-testid="field-hora-inicio" />
                  </div>
                  <div>
                    <label className={labelClass}>Horário previsto de término</label>
                    <input type="time" value={v.horaFim || ''} onChange={set('horaFim')} className={inputClass} data-testid="field-hora-fim" />
                  </div>
                  <div>
                    <label className={labelClass}>Público estimado</label>
                    <input type="text" value={v.publico || ''} onChange={set('publico')} placeholder="Ex: 300 participantes" className={inputClass} data-testid="field-publico" />
                  </div>
                  <div>
                    <label className={labelClass}>Haverá atividades simultâneas em mais de um ambiente?</label>
                    <select value={v.simultaneas || ''} onChange={set('simultaneas')} className={inputClass} data-testid="field-simultaneas">
                      <option value="">Selecione</option>
                      <option>Sim</option>
                      <option>Não</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Instalação permanente */}
              {isInstalacao && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="fields-instalacao">
                  <div>
                    <label className={labelClass}>Tipo de ambiente</label>
                    <input type="text" value={v.ambiente || ''} onChange={set('ambiente')} placeholder="Ex: auditório, sala de reunião, igreja" className={inputClass} data-testid="field-ambiente" />
                  </div>
                  <div>
                    <label className={labelClass}>Finalidade</label>
                    <input type="text" value={v.finalidade || ''} onChange={set('finalidade')} placeholder="Ex: apresentações, cultos, treinamentos" className={inputClass} data-testid="field-finalidade" />
                  </div>
                  <div>
                    <label className={labelClass}>Prazo de implantação</label>
                    <input type="text" value={v.prazo || ''} onChange={set('prazo')} placeholder="Ex: até junho/2027" className={inputClass} data-testid="field-prazo" />
                  </div>
                  <div>
                    <label className={labelClass}>Já possui a planta do local?</label>
                    <select value={v.planta || ''} onChange={set('planta')} className={inputClass} data-testid="field-planta">
                      <option value="">Selecione</option>
                      <option value="Sim, já tenho a planta">Sim, já tenho a planta</option>
                      <option value="Ainda não tenho a planta">Ainda não tenho</option>
                    </select>
                    {v.planta === 'Sim, já tenho a planta' && (
                      <p className="text-xs text-[#00A8E8] font-body mt-1.5">Ótimo! Anexe a planta no campo de anexo ao final do formulário.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Serviços necessários */}
              {need && (
                <div>
                  <label className={labelClass}>Serviços necessários</label>
                  <div className="flex flex-wrap gap-2" data-testid="field-servicos">
                    {SERVICES.map(s => (
                      <button type="button" key={s} onClick={() => toggleList('servicos', s)} data-testid={`servico-${s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        className={`px-4 py-2 rounded-full text-sm font-body border transition-colors ${has(s) ? 'bg-[#0A2463] text-white border-[#0A2463]' : 'bg-white text-[#475569] border-slate-200 hover:border-[#00A8E8]'}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Especificações por serviço */}
              {has('Sonorização') && (
                <SpecBlock title="Sonorização" orientKey="sonOrient" v={v} set={set} testId="spec-sonorizacao">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Quantidade de microfones</label>
                      <input type="text" value={v.sonQtd || ''} onChange={set('sonQtd')} placeholder="Ex: 4" className={inputClass} data-testid="field-son-qtd" />
                    </div>
                    <div>
                      <label className={labelClass}>Áudio para</label>
                      <select value={v.sonUso || ''} onChange={set('sonUso')} className={inputClass} data-testid="field-son-uso">
                        <option value="">Selecione</option>
                        <option>Apresentações/fala</option>
                        <option>Música ao vivo</option>
                        <option>Ambos</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Tipos de microfone</label>
                    <div className="flex flex-wrap gap-2">
                      {MIC_TYPES.map(m => (
                        <button type="button" key={m} onClick={() => toggleList('sonTipos', m)}
                          className={`px-3 py-1.5 rounded-full text-xs font-body border transition-colors ${(v.sonTipos || []).includes(m) ? 'bg-[#00A8E8] text-white border-[#00A8E8]' : 'bg-white text-[#475569] border-slate-200'}`}>
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>
                </SpecBlock>
              )}

              {has('Painéis de LED') && (
                <SpecBlock title="Painéis de LED" orientKey="ledOrient" v={v} set={set} testId="spec-led">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>Quantidade</label>
                      <input type="text" value={v.ledQtd || ''} onChange={set('ledQtd')} placeholder="Ex: 1" className={inputClass} data-testid="field-led-qtd" />
                    </div>
                    <div>
                      <label className={labelClass}>Largura x altura (m)</label>
                      <input type="text" value={v.ledDim || ''} onChange={set('ledDim')} placeholder="Ex: 6m x 3m" className={inputClass} data-testid="field-led-dim" />
                    </div>
                    <div>
                      <label className={labelClass}>Uso</label>
                      <select value={v.ledLocal || ''} onChange={set('ledLocal')} className={inputClass} data-testid="field-led-local">
                        <option value="">Selecione</option>
                        <option>Interno</option>
                        <option>Externo</option>
                      </select>
                    </div>
                  </div>
                </SpecBlock>
              )}

              {has('Projeção') && (
                <SpecBlock title="Projeção" orientKey="projOrient" v={v} set={set} testId="spec-projecao">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Qtd. de telas/projetores</label>
                      <input type="text" value={v.projQtd || ''} onChange={set('projQtd')} placeholder="Ex: 2" className={inputClass} data-testid="field-proj-qtd" />
                    </div>
                    <div>
                      <label className={labelClass}>Dimensões desejadas</label>
                      <input type="text" value={v.projDim || ''} onChange={set('projDim')} placeholder="Ex: telas de 3m x 2m" className={inputClass} data-testid="field-proj-dim" />
                    </div>
                  </div>
                </SpecBlock>
              )}

              {has('Transmissão/Gravação') && (
                <SpecBlock title="Transmissão / Gravação" orientKey="transOrient" v={v} set={set} testId="spec-transmissao">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>Tipo</label>
                      <select value={v.transTipo || ''} onChange={set('transTipo')} className={inputClass} data-testid="field-trans-tipo">
                        <option value="">Selecione</option>
                        <option>Transmissão ao vivo</option>
                        <option>Gravação</option>
                        <option>Ambas</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Plataforma</label>
                      <input type="text" value={v.transPlataforma || ''} onChange={set('transPlataforma')} placeholder="Ex: YouTube, Zoom" className={inputClass} data-testid="field-trans-plataforma" />
                    </div>
                    <div>
                      <label className={labelClass}>Qtd. de ambientes</label>
                      <input type="text" value={v.transAmbientes || ''} onChange={set('transAmbientes')} placeholder="Ex: 1" className={inputClass} data-testid="field-trans-ambientes" />
                    </div>
                  </div>
                </SpecBlock>
              )}

              {has('Estruturas') && (
                <SpecBlock title="Estruturas" orientKey="estrOrient" v={v} set={set} testId="spec-estruturas">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>Tipo de estrutura</label>
                      <input type="text" value={v.estrTipo || ''} onChange={set('estrTipo')} placeholder="Ex: palco, box truss, tenda" className={inputClass} data-testid="field-estr-tipo" />
                    </div>
                    <div>
                      <label className={labelClass}>Quantidade</label>
                      <input type="text" value={v.estrQtd || ''} onChange={set('estrQtd')} placeholder="Ex: 1" className={inputClass} data-testid="field-estr-qtd" />
                    </div>
                    <div>
                      <label className={labelClass}>Dimensões</label>
                      <input type="text" value={v.estrDim || ''} onChange={set('estrDim')} placeholder="Ex: palco 8m x 6m" className={inputClass} data-testid="field-estr-dim" />
                    </div>
                  </div>
                </SpecBlock>
              )}

              {/* Detalhes */}
              <div>
                <label className={labelClass}>Conte os detalhes que souber sobre seu projeto *</label>
                <p className="text-xs text-[#475569] font-body mb-1.5">Complemente as informações acima. Se já tiver medidas, equipamentos ou especificações, inclua aqui.</p>
                <textarea required rows={4} value={v.detalhes || ''} onChange={set('detalhes')}
                  className={`${inputClass} resize-none`} data-testid="field-detalhes" />
              </div>

              {/* Anexo opcional */}
              <div>
                <label className={labelClass}>Anexo <span className="font-normal text-slate-400">(opcional)</span></label>
                <p className="text-xs text-[#475569] font-body mb-1.5">Briefing, planta do local, projeto, edital, fotos ou lista de equipamentos (PDF, imagens, Word, Excel — máx. 10MB).</p>
                {attachment ? (
                  <div className="flex items-center gap-2 bg-[#00A8E8]/10 border border-[#00A8E8]/30 rounded-xl px-4 py-3 text-sm text-[#0A2463] font-body" data-testid="attachment-chip">
                    <Paperclip size={15} className="text-[#00A8E8] flex-shrink-0" />
                    <span className="truncate flex-1">{attachment.name}</span>
                    <button type="button" onClick={() => { setAttachment(null); if (fileRef.current) fileRef.current.value = ''; }}
                      className="text-slate-400 hover:text-red-500" data-testid="remove-attachment"><X size={16} /></button>
                  </div>
                ) : (
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="attach-btn"
                    className="w-full border-2 border-dashed border-slate-200 hover:border-[#00A8E8] rounded-xl px-4 py-4 text-sm text-[#475569] font-body flex items-center justify-center gap-2 transition-colors">
                    <Paperclip size={16} /> {uploading ? 'Enviando arquivo...' : 'Clique para anexar um arquivo'}
                  </button>
                )}
                <input ref={fileRef} type="file" className="hidden" onChange={handleFile}
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip" data-testid="attach-input" />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-3 text-sm font-body" data-testid="contact-error">{error}</div>
              )}

              <button type="submit" disabled={loading || uploading} data-testid="contact-submit-btn"
                className="w-full bg-[#0A2463] hover:bg-[#0A2463]/90 disabled:opacity-60 text-white py-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg font-body text-sm sm:text-base">
                {loading ? 'Enviando...' : <><Send size={18} /> Solicitar orçamento</>}
              </button>
            </motion.form>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
